// ABOUTME: Container component that fetches calendar events for a child and renders the DayCalendar.
// ABOUTME: Handles loading, error states, day navigation, stale data indicator, all-day events strip, and auto-refresh.

import { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Skeleton, Typography, Chip, Alert } from '@mui/material';
import { DayCalendar, CalendarDisplayEvent } from './DayCalendar';
import { DayCalendarNav } from './DayCalendarNav';
import { calendarAPI } from '../../services/api';
import { useTimezone } from '../../contexts/TimezoneContext';
import { utcToFamilyTimezoneDisplay } from '../../utils/dateUtils';
import type { CalendarEventDto } from '../../types/api';

// Number of all-day events visible before the scroll area activates
const ALL_DAY_SCROLL_THRESHOLD = 3;

// How often to re-fetch calendar events in the background (10 minutes)
const AUTO_REFRESH_INTERVAL_MS = 10 * 60 * 1000;

interface ChildCalendarPanelProps {
  childId: number;
  externalDate?: Date;
}

export function ChildCalendarPanel({ childId, externalDate }: ChildCalendarPanelProps) {
  const { familyTimezone, getTodayInFamilyTimezone, getDateInFamilyTimezone } = useTimezone();
  // selectedDate starts null — initialized to family-timezone today once timezone loads
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [events, setEvents] = useState<CalendarDisplayEvent[]>([]);
  const [allDayEvents, setAllDayEvents] = useState<CalendarEventDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isStale, setIsStale] = useState(false);
  // Tracks which timezone was used to initialize selectedDate — re-initializes when timezone changes
  const initializedForTimezone = useRef<string | undefined>(undefined);
  // Tracks whether the user has manually navigated away from today — suppresses date auto-advance
  const userHasNavigatedRef = useRef(false);

  // Initialize selectedDate to today in family timezone, and re-initialize when timezone changes.
  // Uses T12:00:00Z (noon UTC) so getDateInFamilyTimezone returns the correct date regardless
  // of the admin's browser timezone (avoids local-midnight crossing into previous day in far-west zones).
  useEffect(() => {
    if (familyTimezone && initializedForTimezone.current !== familyTimezone) {
      initializedForTimezone.current = familyTimezone;
      userHasNavigatedRef.current = false;
      setSelectedDate(new Date(getTodayInFamilyTimezone() + 'T12:00:00Z'));
    }
  }, [familyTimezone, getTodayInFamilyTimezone]);

  // Sync internal selectedDate when externalDate prop changes (e.g. heart calendar selection).
  // Only suppress auto-advance for non-today dates — if externalDate is today, leave
  // userHasNavigatedRef false so midnight auto-advance still works.
  useEffect(() => {
    if (externalDate && familyTimezone) {
      setSelectedDate(externalDate);
      const extDateStr = getDateInFamilyTimezone(externalDate);
      userHasNavigatedRef.current = extDateStr !== getTodayInFamilyTimezone();
    }
  }, [externalDate, familyTimezone, getDateInFamilyTimezone, getTodayInFamilyTimezone]);

  const fetchEvents = useCallback(async (date: Date) => {
    setIsLoading(true);
    setErrorMessage(undefined);

    try {
      const dateStr = getDateInFamilyTimezone(date);
      const result = await calendarAPI.getChildEvents(childId, dateStr, familyTimezone);

      // Show any successfully-fetched events even if some calendars failed
      // Convert UTC event times to family timezone "fake local" for react-big-calendar display
      const tz = familyTimezone ?? 'UTC';
      const displayEvents: CalendarDisplayEvent[] = result.events.map(e => ({
        title: e.title,
        start: utcToFamilyTimezoneDisplay(new Date(e.startTime), tz),
        end: utcToFamilyTimezoneDisplay(new Date(e.endTime), tz),
      }));
      setEvents(displayEvents);
      setAllDayEvents(result.allDayEvents ?? []);

      if (result.errorMessage) {
        setErrorMessage(result.errorMessage);
      }
      setIsStale(result.isStale);
    } catch {
      setErrorMessage('Failed to load calendar events');
      setEvents([]);
      setAllDayEvents([]);
    } finally {
      setIsLoading(false);
    }
  }, [childId, familyTimezone, getDateInFamilyTimezone]);

  useEffect(() => {
    if (selectedDate) {
      fetchEvents(selectedDate);
    }
  }, [selectedDate, fetchEvents]);

  // Auto-refresh: re-fetch events every 10 minutes and advance to new today if date has rolled over
  useEffect(() => {
    if (!selectedDate || !familyTimezone) return;

    const intervalId = setInterval(() => {
      // If user hasn't manually navigated, check whether midnight has passed since the view opened
      if (!userHasNavigatedRef.current) {
        const todayStr = getTodayInFamilyTimezone();
        const currentStr = getDateInFamilyTimezone(selectedDate);
        if (todayStr !== currentStr) {
          // Advance to the new today. Changing selectedDate triggers the fetch useEffect above,
          // which calls fetchEvents automatically — no explicit fetchEvents call needed here.
          setSelectedDate(new Date(todayStr + 'T12:00:00Z'));
          return;
        }
      }
      fetchEvents(selectedDate);
    }, AUTO_REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [selectedDate, familyTimezone, fetchEvents, getTodayInFamilyTimezone, getDateInFamilyTimezone]);

  const handleDateChange = (newDate: Date) => {
    userHasNavigatedRef.current = true;
    setSelectedDate(newDate);
  };

  const handleToday = () => {
    userHasNavigatedRef.current = false;
    setSelectedDate(new Date(getTodayInFamilyTimezone() + 'T12:00:00Z'));
  };

  // Show loading skeleton until family timezone and initial date are ready
  if (!familyTimezone || !selectedDate) {
    return (
      <Box data-testid="calendar-timezone-loading" sx={{ mt: 1, flex: 1 }}>
        <Skeleton variant="rectangular" height={200} />
      </Box>
    );
  }

  const isToday = getDateInFamilyTimezone(selectedDate) === getTodayInFamilyTimezone();
  const hasScrollArea = allDayEvents.length > ALL_DAY_SCROLL_THRESHOLD;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <DayCalendarNav
        date={selectedDate}
        onDateChange={handleDateChange}
        onToday={handleToday}
        isToday={isToday}
      />

      {isStale && !errorMessage && (
        <Chip
          label="Data may be outdated"
          size="small"
          variant="outlined"
          color="warning"
          sx={{ alignSelf: 'center', mt: 0.5 }}
        />
      )}

      {errorMessage && (
        <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
          <Typography variant="body2">{errorMessage}</Typography>
        </Alert>
      )}

      {allDayEvents.length > 0 && (
        <Box data-testid="all-day-events-strip" sx={{ mt: 0.5 }}>
          <Box
            data-testid={hasScrollArea ? 'all-day-events-scroll' : undefined}
            sx={hasScrollArea ? { maxHeight: 96, overflowY: 'auto' } : undefined}
          >
            {allDayEvents.map((event, index) => (
              <Typography
                key={index}
                variant="body2"
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  backgroundColor: 'action.hover',
                  mb: 0.25,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {event.title}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {isLoading ? (
        <Box data-testid="calendar-loading" sx={{ mt: 1, flex: 1 }}>
          <Skeleton variant="rectangular" height={200} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, mt: 0.5 }}>
          <DayCalendar events={events} date={selectedDate} familyTimezone={familyTimezone} />
        </Box>
      )}
    </Box>
  );
}
